/* ===========================================================================
  Bossi | Header behaviour (sections/bossi-header.liquid)
  - Panels open on hover with a short delay, stay open while the pointer is over the link or its
    panel, and close a moment after it leaves; click, Enter, Escape and Tab all work.
  - Transparent over a full-width opening photo; solid once scrolled or while a panel is open.
  - Slides away while scrolling down, returns on scrolling up (after a clear scroll each way, not
    the small accidental ones a trackpad or mouse makes).
  - Phone menu in a full-screen dialog.
  - Publishes the header height (--b-header-h, and Horizon's --header-height).
============================================================================ */

const OPEN_DELAY = 90;
const CLOSE_DELAY = 240;
// The header only slides away or comes back after a clear scroll in one direction, so the small
// accidental scrolls a trackpad or Magic Mouse makes as the hand moves do not move the bars
// under the pointer (Build yours in the in-page bar moved away just as it was clicked).
const HIDE_AFTER = 24;
const SHOW_AFTER = 64;
const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
const desktop = window.matchMedia('(min-width: 990px)');

class BossiHeader extends HTMLElement {
  connectedCallback() {
    this.bar = this.querySelector('.b-header__bar');
    this.items = [...this.querySelectorAll('[data-b-nav-item]')].filter((li) => li.querySelector('[data-b-dd]'));
    this.menu = document.getElementById('BossiMenu');
    this.openItem = null;
    this.timers = new Map();
    this.lastY = 0;

    this.setupOverlay();
    this.measure();
    this.setupPanels();
    this.setupScroll();
    this.setupMenu();

    this.resizeObserver = new ResizeObserver(() => this.measure());
    this.resizeObserver.observe(this.bar);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.openItem) {
        const trigger = this.openItem.querySelector('[data-b-nav-trigger]');
        this.close(this.openItem);
        trigger?.focus();
      }
    });
    this.querySelector('[data-b-scrim]')?.addEventListener('click', () => this.openItem && this.close(this.openItem));
  }

  /* ---------- Size ---------- */

  measure() {
    const h = Math.round(this.bar.getBoundingClientRect().height);
    document.documentElement.style.setProperty('--b-header-h', `${h}px`);
    document.body.style.setProperty('--header-height', `${h}px`);
    document.body.style.setProperty('--header-group-height', `${h}px`);
  }

  /* ---------- Transparent over an opening photo ---------- */

  setupOverlay() {
    if (!this.classList.contains('b-header--can-overlay')) return;
    const first = document.querySelector('#MainContent > .shopify-section:first-child');
    const hero = first?.querySelector('[data-b-overlay-hero]');
    if (hero) {
      this.classList.add('is-overlay');
      document.documentElement.classList.add('b-has-overlay');
    }
  }

  /* ---------- Scrolling ---------- */

  get scroller() {
    const wrapper = document.querySelector('.page-wrapper');
    if (wrapper && desktop.matches && getComputedStyle(wrapper).overflowY !== 'visible') return wrapper;
    return window;
  }

  setupScroll() {
    const onScroll = () => {
      const source = this.scroller;
      const y = source === window ? window.scrollY : source.scrollTop;
      this.classList.toggle('is-scrolled', y > 8);
      document.documentElement.classList.toggle('b-header-visible', !this.classList.contains('is-hidden'));
      if (this.dataset.sticky === 'scroll-up') {
        const h = this.bar.offsetHeight;
        const dy = y - this.lastY;
        // Distance travelled in the current direction, within one scrolling movement (a pause
        // starts a new one, so separate little nudges never add up).
        const now = performance.now();
        if (now - (this.lastScrollAt || 0) > 300) this.travel = 0;
        this.lastScrollAt = now;
        if (dy && Math.sign(dy) !== Math.sign(this.travel || 0)) this.travel = 0;
        this.travel = (this.travel || 0) + dy;
        if (y <= h) this.classList.remove('is-hidden');
        else if (this.travel >= HIDE_AFTER && y > h * 2 && !this.openItem && !this.menu?.open) this.classList.add('is-hidden');
        else if (this.travel <= -SHOW_AFTER) this.classList.remove('is-hidden');
        document.documentElement.classList.toggle('b-header-visible', !this.classList.contains('is-hidden'));
      }
      this.lastY = y;
    };
    const attach = () => {
      window.removeEventListener('scroll', onScroll);
      document.querySelector('.page-wrapper')?.removeEventListener('scroll', onScroll);
      this.scroller.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    };
    attach();
    desktop.addEventListener('change', attach);
  }

  /* ---------- Panels ---------- */

  setupPanels() {
    this.items.forEach((item) => {
      const trigger = item.querySelector('[data-b-nav-trigger]');
      const panel = item.querySelector('[data-b-dd]');
      item.addEventListener('pointerenter', (e) => {
        if (e.pointerType !== 'mouse' || !desktop.matches) return;
        this.clear(item);
        if (this.openItem && this.openItem !== item) this.open(item);
        else this.timers.set(item, setTimeout(() => this.open(item), OPEN_DELAY));
      });
      item.addEventListener('pointerleave', (e) => {
        if (e.pointerType !== 'mouse' || !desktop.matches) return;
        this.clear(item);
        this.timers.set(item, setTimeout(() => this.close(item), CLOSE_DELAY));
      });
      trigger.addEventListener('click', () => {
        if (this.openItem === item) this.close(item);
        else this.open(item);
      });
      item.addEventListener('focusout', (e) => {
        if (!item.contains(e.relatedTarget)) {
          this.clear(item);
          this.timers.set(item, setTimeout(() => this.close(item), CLOSE_DELAY));
        }
      });
      item.addEventListener('focusin', () => this.clear(item));
      panel.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.close(item);
          trigger.focus();
        }
      });
    });
  }

  clear(item) {
    clearTimeout(this.timers.get(item));
    this.timers.delete(item);
  }

  open(item) {
    if (this.openItem && this.openItem !== item) this.close(this.openItem, true);
    const trigger = item.querySelector('[data-b-nav-trigger]');
    const panel = item.querySelector('[data-b-dd]');
    panel.hidden = false;
    requestAnimationFrame(() => panel.classList.add('is-open'));
    trigger.setAttribute('aria-expanded', 'true');
    item.classList.add('is-open');
    this.classList.add('is-panel-open');
    this.classList.remove('is-hidden');
    this.openItem = item;
  }

  close(item, immediate = false) {
    const trigger = item.querySelector('[data-b-nav-trigger]');
    const panel = item.querySelector('[data-b-dd]');
    trigger.setAttribute('aria-expanded', 'false');
    item.classList.remove('is-open');
    panel.classList.remove('is-open');
    const done = () => {
      if (!item.classList.contains('is-open')) panel.hidden = true;
    };
    if (immediate || reduce.matches) done();
    else setTimeout(done, 260);
    if (this.openItem === item) {
      this.openItem = null;
      this.classList.remove('is-panel-open');
    }
  }

  /* ---------- Phone menu ---------- */

  setupMenu() {
    const openButton = this.querySelector('[data-b-menu-open]');
    if (!this.menu || !openButton) return;
    const closeButton = this.menu.querySelector('[data-b-menu-close]');
    const open = () => {
      this.menu.showModal();
      requestAnimationFrame(() => {
        this.menu.classList.add('is-open');
        this.menu.querySelector('[data-b-stagger]')?.classList.add('is-in');
      });
      openButton.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('b-menu-open');
    };
    const close = () => {
      this.menu.classList.remove('is-open');
      openButton.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('b-menu-open');
      setTimeout(() => this.menu.open && this.menu.close(), reduce.matches ? 0 : 320);
    };
    openButton.addEventListener('click', open);
    closeButton?.addEventListener('click', close);
    this.menu.addEventListener('cancel', (e) => {
      e.preventDefault();
      close();
    });
    this.menu.addEventListener('click', (e) => {
      if (e.target instanceof HTMLAnchorElement) close();
    });
    desktop.addEventListener('change', () => desktop.matches && this.menu.open && close());
  }
}

if (!customElements.get('bossi-header')) customElements.define('bossi-header', BossiHeader);
